/**
 * The player canvas's backing store (spec 012): the buffer is the stage size
 * times the device pixel ratio (capped at `MAX_PIXEL_RATIO`), its CSS size
 * stays the stage size, and drawing runs in stage px under a scale transform
 * with high-quality image smoothing. Setting a canvas's width or height
 * resets its context state, so `prepareStageContext` runs before every frame.
 * No DOM beyond the canvas and `window.devicePixelRatio` (read when present).
 */

/** The highest pixel ratio the buffer follows (a 375×667 stage at 3 is 1125×2001). */
export const MAX_PIXEL_RATIO = 3;

/** The pixel ratio the buffer follows: `devicePixelRatio` (1 when absent or not positive), at most `MAX_PIXEL_RATIO`. */
export function canvasPixelRatio(
  raw: unknown = typeof window !== 'undefined' ? window.devicePixelRatio : undefined,
): number {
  const r = Number(raw);
  return Number.isFinite(r) && r > 0 ? Math.min(r, MAX_PIXEL_RATIO) : 1;
}

/** The buffer size, in device px, of a `width`×`height` stage at `ratio`. */
export function canvasBufferSize(width: number, height: number, ratio: number): { width: number; height: number } {
  return { width: Math.max(1, Math.round(width * ratio)), height: Math.max(1, Math.round(height * ratio)) };
}

/**
 * Size `canvas`'s buffer for a `width`×`height` stage at `ratio`; true when
 * it changed (a resized buffer is blank and its context state reset).
 */
export function sizeCanvasBuffer(
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
  ratio: number = canvasPixelRatio(),
): boolean {
  const size = canvasBufferSize(width, height, ratio);
  let changed = false;
  if (canvas.width !== size.width) { canvas.width = size.width; changed = true; }
  if (canvas.height !== size.height) { canvas.height = size.height; changed = true; }
  return changed;
}

/**
 * Draw in stage px on a buffer of `scaleX`×`scaleY` device px per stage px:
 * the transform maps stage px onto the buffer, image smoothing on and 'high'.
 */
export function prepareStageContext(ctx: CanvasRenderingContext2D, scaleX: number, scaleY: number = scaleX): void {
  ctx.setTransform(scaleX, 0, 0, scaleY, 0, 0);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
}
